import React, { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { debounce } from '@amzn/storm-ui-utils';
import {
  SIDEBAR_TYPE,
  SideNavLinkItemType,
} from '@amzn/aac-chrome-internal-api';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import {
  Overlay,
  ToolbarWrapper,
  ToolbarOutterWrapper,
  AllToolsHiddenContent,
  NavItemsContainer,
} from 'src/features/nav/components/ConsolidatedSideNav/styles';
import SideNavLink from 'src/features/nav/components/ConsolidatedSideNav/SideNavLink';
import Accordion from 'src/features/nav/components/ConsolidatedSideNav/Accordion/Accordion';
import SideNavModal from './SideNavModal';
import SideNavButton from './SideNavButton';
import AllToolsNavItem from './AllToolsComponents/AllToolsNavItem';
import SideNavAllToolsButton from './AllToolsComponents/SideNavAllToolsButton';
import {
  CC_MODAL_BULLSEYE_TOOLBAR_ID,
  WeblabedFeatures,
} from 'src/commons/constants';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { resolveEffectiveA3cAgent } from 'src/commons/utils/a3cChatGate';
import { isEmbeddedModeUrl } from 'src/commons/utils/a3cChatUrlRules';
import {
  useDomRefById,
  useIsFeatureEnabled,
  useA3cEverywhereEnabled,
} from 'src/commons/hooks';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import { InterstitialErrorBoundary } from './InterstitialModal/InterstitialErrorBoundary';
import AdsAgentNavButton from './AdsAgentNavButton';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { loadInterstitialModalRemote } from './InterstitialModal/loadInterstitialRemote';
import { getSingleAppLink } from 'src/features/nav/utils/toolbarItemLinks';

const InterstitialModal = React.lazy(
  () =>
    loadInterstitialModalRemote() as Promise<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      default: React.ComponentType<any>;
    }>,
);

const OverlayContainer = () => {
  const [portalElement, setPortalElement] = useState<Element | null>(null);
  useEffect(() => {
    const portal = document.getElementById('AACChromePortal');
    if (portal && document.body.contains(portal)) {
      setPortalElement(portal);
    }
    return () => {
      setPortalElement(null);
    };
  }, []);

  if (!portalElement) {
    return null;
  }

  return createPortal(
    <Overlay data-ccx-e2e-id="chrome-overlay" data-test-id="chrome-overlay" />,
    portalElement,
  );
};

export const ConsolidatedSideNavInner: React.VFC = () => {
  const {
    isQuickNavModalEnabled,
    allToolbarItems,
    isNavOpen,
    isAllToolsOpen,
    setIsNavOpen,
    setIsAllToolsOpen,
    toolbarItems,
    showInterstitialModal,
    interstitialDestinationUrl,
    setInterstitialDestinationUrl,
    interstitialAppName,
    setInterstitialAppName,
  } = useConsolidatedNavContext();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  // This ref is required so that we can access the current isFocused value inside the debounce function below
  const isFocusedReference = useRef<boolean>();
  const hasLoggedNavOpen = useRef<boolean>(false);
  const hasLoggedAllToolsOpen = useRef<boolean>(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const portalRef = useDomRefById('AACChromePortal');
  const sidenavPopoverPortalRef = useDomRefById('AACChromeGlobalNavPortal');
  isFocusedReference.current = isFocused;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<string>('auto');

  const allToolsEnabled = !!allToolbarItems?.length;

  const a3cConfig = useChromeConfigContext()?.a3cConfig;
  // Path-aware weblab selection shared with EmbeddedChatManager: on /cm and
  // /campaign-manager the UCM weblab is the sole control of the everywhere
  // chat, and the sidenav DOES render there — gating the button on T1/T2
  // while the panel loads under the UCM weblab would show a button without
  // a chat (or hide it while the chat loads). UCB paths are focus-mode
  // builders with no sidenav, so the UCB arm is moot here but harmless.
  const isA3cEverywhereEnabled = useA3cEverywhereEnabled();
  const isInterstitialTrimT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_INTERSTITIAL_TO_TRIM,
  );
  const isInterstitialTrimT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_INTERSTITIAL_TO_TRIM_T2,
  );
  const interstitialDataMode = isInterstitialTrimT2Enabled
    ? 'trim-shadow'
    : isInterstitialTrimT1Enabled
      ? 'trim'
      : 'control';
  // Whether the button renders. Shared with EmbeddedChatManager (panel) and
  // the focus-header Ads Agent toggle via resolveEffectiveA3cAgent, so a
  // page-provided empty selectedAgentIdentifier hides all three surfaces
  // consistently. The helper also folds in the page-level chat vetoes
  // (loadBehavior DISABLED, chat-disabled URL). The AAO unified gate arrives
  // through isA3cEverywhereEnabled instead, so it withholds the everywhere
  // default without hiding a page-provided legacy agent's button.
  const shouldShowAdsAgentButton = !!resolveEffectiveA3cAgent({
    a3cConfig,
    isEmbeddedModeForcedByUrl: isEmbeddedModeUrl(window.location.href),
    isA3cEverywhereEnabled,
  });
  const { config } = useInternalChromeConfigContext();

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isNavOpen && isAllToolsOpen) {
      setIsAllToolsOpen(false);
    }
  }, [isNavOpen]);

  useEffect(() => {
    if (isNavOpen && !hasLoggedNavOpen.current) {
      logWithTakt('aac-chrome-global-navigation-open', null);
      hasLoggedNavOpen.current = true;
    }
  }, [isNavOpen && !hasLoggedNavOpen.current]);

  useEffect(() => {
    if (isAllToolsOpen && !hasLoggedAllToolsOpen.current) {
      logWithTakt('aac-chrome-all-tools-panel-open', null);
      hasLoggedAllToolsOpen.current = true;
    }
  }, [isAllToolsOpen]);

  useEffect(() => {
    if (containerRef.current && document.body.contains(containerRef.current)) {
      const container = containerRef.current;
      const width = container.scrollWidth;
      setContainerWidth(`${width}px`);
    }
  }, [allToolsEnabled]);

  const handleEnterNav = (e: React.MouseEvent<HTMLElement>) => {
    const target = e?.target as HTMLElement;
    if (portalRef?.current?.contains(target)) {
      return;
    }
    if (sidenavPopoverPortalRef?.current?.contains(target)) {
      return;
    }
    if (!isNavOpen) {
      setIsNavOpen(true);
    }
  };

  const handleLeaveNav = () => {
    setIsNavOpen(false);
  };

  const handleClose = () => {
    setInterstitialDestinationUrl('');
    setInterstitialAppName('');
  };

  const handleEnterTools = (e: React.MouseEvent<HTMLElement>) => {
    const target = e?.target as HTMLElement;
    if (portalRef?.current?.contains(target)) {
      return;
    }
    if (sidenavPopoverPortalRef?.current?.contains(target)) {
      return;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (!isNavOpen) {
      setIsNavOpen(true);
    }
    if (!isAllToolsOpen) {
      setIsAllToolsOpen(true);
    }
  };

  const handleLeaveTools = (delay: boolean) => {
    if (delay) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsAllToolsOpen(false);
      }, 300);
    } else {
      setIsAllToolsOpen(false);
    }
  };

  return (
    <>
      <ToolbarOutterWrapper
        isNavOpen={isNavOpen}
        isAllToolsOpen={isAllToolsOpen}
        onMouseEnter={(e) => {
          if (!hasInteracted) {
            setHasInteracted(true);
          }
          handleEnterNav(e);
        }}
        onMouseOver={handleEnterNav}
        onMouseLeave={handleLeaveNav}
        onFocus={(e) => {
          const target = e?.target as HTMLElement;
          if (portalRef?.current?.contains(target)) {
            return;
          }
          if (sidenavPopoverPortalRef?.current?.contains(target)) {
            return;
          }
          if (!hasInteracted) {
            setHasInteracted(true);
          }
          setIsFocused(true);
          !isNavOpen && setIsNavOpen(true);
        }}
        onBlur={(e) => {
          const target = e?.target as HTMLElement;
          if (portalRef?.current?.contains(target)) {
            return;
          }
          if (sidenavPopoverPortalRef?.current?.contains(target)) {
            return;
          }
          setIsFocused(false);
          debounce(
            () => !isFocusedReference.current && setIsNavOpen(false),
            100,
          )();
        }}
        data-ccx-e2e-id="chrome-side-nav"
      >
        <ToolbarWrapper
          isNavOpen={isNavOpen}
          onFocus={(e) => {
            const target = e?.target as HTMLElement;
            if (portalRef?.current?.contains(target)) {
              return;
            }
            if (sidenavPopoverPortalRef?.current?.contains(target)) {
              return;
            }
            setIsFocused(true);
            !isNavOpen && setIsNavOpen(true);
          }}
          onBlur={(e) => {
            const target = e?.target as HTMLElement;
            if (portalRef?.current?.contains(target)) {
              return;
            }
            if (sidenavPopoverPortalRef?.current?.contains(target)) {
              return;
            }
            setIsFocused(false);
            // This is required because shifting the focus from one nav element to another triggers onBlur and then immediately triggers onFocus handler. We want to await for 100 ms to make sure other nav elements are not focused before collapsing the nav
            debounce(
              () => !isFocusedReference.current && setIsNavOpen(false),
              100,
            )();
          }}
        >
          <NavItemsContainer>
            {shouldShowAdsAgentButton && (
              <ErrorBoundary name="AdsAgentNavButton">
                <AdsAgentNavButton isNavOpen={isNavOpen} />
              </ErrorBoundary>
            )}
            {toolbarItems
              .filter(
                (toolbarItem) =>
                  toolbarItem.sidebarType !== SIDEBAR_TYPE.DIVIDER &&
                  toolbarItem.id !== CC_MODAL_BULLSEYE_TOOLBAR_ID,
              )
              .map((toolbarItem) => {
                const singleAppLink = getSingleAppLink(toolbarItem);
                if (toolbarItem.sidebarType === SIDEBAR_TYPE.ALL_TOOLS) {
                  return (
                    <ErrorBoundary
                      key={toolbarItem.id}
                      name="SideNavAllToolsButton"
                    >
                      <SideNavAllToolsButton
                        toolbarItem={toolbarItem}
                        onMouseEnter={handleEnterTools}
                        onMouseLeave={() => handleLeaveTools(true)}
                      />
                    </ErrorBoundary>
                  );
                }
                if (singleAppLink) {
                  if (
                    singleAppLink.sidebarItemType ===
                    ('MODAL' as SideNavLinkItemType)
                  ) {
                    return (
                      <SideNavButton
                        key={toolbarItem.id}
                        id={toolbarItem.id}
                        allToolsEnabled={allToolsEnabled}
                      />
                    );
                  }
                  return (
                    <SideNavLink
                      key={toolbarItem.id}
                      id={toolbarItem.id}
                      isQuickNavModalEnabled={isQuickNavModalEnabled}
                      allToolsEnabled={allToolsEnabled}
                    />
                  );
                }
                return (
                  <Accordion
                    key={toolbarItem.id}
                    id={toolbarItem.id}
                    allToolsEnabled={allToolsEnabled}
                  />
                );
              })}
          </NavItemsContainer>
        </ToolbarWrapper>
        {!!allToolbarItems?.length && (
          <ErrorBoundary name="AllToolsHiddenContent">
            <AllToolsHiddenContent
              ref={containerRef}
              width={containerWidth}
              onMouseEnter={handleEnterTools}
              onMouseLeave={() => handleLeaveTools(false)}
              onFocus={() => {
                setIsFocused(true);
                !isAllToolsOpen && setIsAllToolsOpen(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                debounce(
                  () => !isFocusedReference.current && setIsAllToolsOpen(false),
                  100,
                )();
              }}
            >
              {allToolbarItems?.map((item, index) => (
                <AllToolsNavItem
                  toolbarItem={item}
                  key={index}
                  data-takt-id={index}
                />
              ))}
            </AllToolsHiddenContent>
          </ErrorBoundary>
        )}
      </ToolbarOutterWrapper>
      {isNavOpen && <OverlayContainer />}
      <SideNavModal />
      {hasInteracted && showInterstitialModal && (
        <InterstitialErrorBoundary entityId={config.entityId}>
          <Suspense fallback={null}>
            <InterstitialModal
              isOpen={!!interstitialDestinationUrl}
              targetApp={interstitialAppName}
              destinationUrl={interstitialDestinationUrl}
              entityId={config.entityId}
              marketplaceId={config.marketplaceId}
              locale={config.locale}
              isMobile={false}
              modalElementId={'AACChromePortal'}
              dataMode={interstitialDataMode}
              onClose={handleClose}
            />
          </Suspense>
        </InterstitialErrorBoundary>
      )}
    </>
  );
};
